<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    :totalSections="1"
  />
  <OperationFormLayout
    v-if="!loading && currentOrganisation"
    :id="mineId"
    :formDisabled="!imageValid"
    :formSaving="saving"
    operationType="mine"
    @cancel="$router.replace({ name: 'mineConfiguration' })"
    @update="save"
  >
    <OperationFormSection
      :modifiers="['border-radius-top', 'border-radius-bottom']"
      :sectionDescription="$t('configuration.mine.details_description')"
      :sectionTitle="$t('configuration.mine.details_title')"
    >
      <FormRow
        :errors="errors?.mine?.name"
        :label="$t('configuration.mine.name_label')"
        labelFor="mine_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': errors?.mine?.name }">
            <input
              id="mine_name"
              v-model="mine.name"
              :placeholder="$t('configuration.mine.input_name')"
              @input="validateInput('mine', 'name')"
            >
          </div>
        </div>
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>
</template>

<script setup>
import { onMounted, provide, reactive, ref } from 'vue';

// Utils
import { useOrganisation } from '@/functions/use_remote_object';
import { useRouter } from 'vue-router';
import useAjax from '@/functions/use_ajax';

// Components
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';

const createNewMine = () => ({
  name: '',
});

const props = defineProps({
  organisationId: { type: String, required: true },
  mineId: { type: String, default: null },
});

const router = useRouter();
const ajax = useAjax();
const currentOrganisation = useOrganisation(props);
const loading = ref(false);
const initialMine = ref(null);
const mine = reactive(createNewMine());
const errors = ref(null);
const saving = ref(false);

const imageValid = ref(true);

provide('currentOrganisation', currentOrganisation);

// All endpoints that this component uses
const ENDPOINTS = {
  mines: `/organisations/${props.organisationId}/mines`,
  mine: `/organisations/${props.organisationId}/mines/${props.mineId}`,
  get post_or_patch() {
    return props.mineId ? this.mine : this.mines;
  },
};

const loadMine = async () => {
  if (props.mineId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.mine);

    if (ok) {
      initialMine.value = data;
      Object.assign(mine, data);
      loading.value = false;
    }
  } else {
    initialMine.value = createNewMine();
  }
};

onMounted(() => {
  loadMine();
});

const formatMineForPost = async () => {
  let mineParams = {
    name: mine.name,
  };

  return { mine: mineParams };
};

const save = async () => {
  errors.value = null;
  saving.value = true;

  let method = props.mineId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatMineForPost(),
  });

  if (ok) {
    router.replace({ name: 'mineConfiguration' });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

function validateInput(type, field) {
  if (errors.value && errors.value[type]) {
    errors.value[type][field] = null;
  }
}
</script>
